import { Link, useNavigate } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { Check, Clock, FileCheck, Lightbulb, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useSidebar } from '@/components/ui/sidebar'

const WrapperComponent = ({
  children,
  id,
  courseId,
  isNextProject,
  isInProgress,
  isCompleted,
  isReturned,
  isAssignmentClosed
}) => {
  const navigate = useNavigate()
  const { setOpen, isMobile, openMobile, setOpenMobile } = useSidebar()

  const handleClick = () => {
    if (isMobile && openMobile) {
      setOpenMobile(false)
    } else {
      setOpen(false)
    }
    navigate(`/students/courses/${courseId}/lessons/${id}`)
  }

  return (
    <Choose>
      <When condition={isInProgress || isCompleted || isReturned || isNextProject}>
        <Card className={twMerge(
          'p-0 hover:bg-accent hover:cursor-pointer shadow-none',
          isNextProject && !isAssignmentClosed ? 'border-2 border-primary' : 'border-1 border-border'
        )}>
          <div
            className="flex flex-row w-full p-3"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
          >
            {children}
          </div>
        </Card>
      </When>

      <Otherwise>
        <Card className='flex flex-row w-full p-3 bg-muted hover:cursor-disabled shadow-none'>
          {children}
        </Card>
      </Otherwise>
    </Choose>
  )
}

const Lesson = ({
  id,
  name,
  status,
  courseId,
  isNextProject,
  isAssignmentClosed,
  score,
  totalPossibleScore,
  isCourseInsightsEnabled
}) => {
  return (
    <WrapperComponent
      id={id}
      courseId={courseId}
      isCompleted={status === 'COMPLETED'}
      isInProgress={status === 'IN_PROGRESS'}
      isReturned={status === 'RETURNED'}
      isNextProject={isNextProject}
      isAssignmentClosed={isAssignmentClosed}
    >

      <div className='w-full text-left flex flex-row items-center'>
        <div className='flex flex-col flex-wrap'>
          <p className='text-lg font-semibold'>{name}</p>

          <div className='flex flex-row items-center'>
            <Choose>
              <When condition={status === 'IN_PROGRESS'}>
                <p className='text-sm inline-block'>In progress</p>
            <Clock className='h-4 w-4 inline-block ml-1' />
          </When>

          <When condition={status === 'COMPLETED'}>
            <p className='text-sm inline-block'>Completed</p>
            <Check className='h-4 w-4 inline-block ml-1' />
          </When>

          <When condition={status === 'RETURNED'}>
            <p className='text-sm inline-block'>Returned</p>
            <FileCheck className='h-4 w-4 inline-block ml-1' />
          </When>

          <Otherwise>
            <p className='text-sm text-gray-500'>Not started</p>
          </Otherwise>
        </Choose>
        </div>
        </div>
      </div>

      <If condition={(status === 'COMPLETED') && isCourseInsightsEnabled}>
        <Link to={`/students/courses/${courseId}/lessons/${id}/insights`}>
          <Button variant="link" className="text-sm">
            <Lightbulb className="h-4 w-4" />
            Insights
          </Button>
        </Link>
      </If>

      <If condition={status === 'RETURNED' && totalPossibleScore > 0}>
        <div className="flex items-center justify-center rounded-md bg-muted px-2 py-1 text-sm font-medium text-muted-foreground">
          <span className="text-primary">{score}</span>
          <span className="mx-1">/</span>
          <span>{totalPossibleScore}</span>
        </div>
      </If>

      <If condition={status === 'IN_PROGRESS'}>
        <Button theme='primary'>
          <Play className='h-4 w-4' />
          Resume
        </Button>
      </If>
    </WrapperComponent>
  )
}

export default Lesson
